import React, { useState, useEffect, useRef } from "react";
import { useOutletContext, useSearchParams } from "react-router-dom";
import { Box, Stack, Alert, IconButton, Typography } from "@mui/material";
import { useTheme as useMuiTheme } from "@mui/material/styles";
import {
  UserPlus,
  ArrowLeft,
  ChevronLeft,
  Users,
  Package,
  LayoutGrid,
  List,
  Building2,
  UserRound,
  Calendar,
  LockKeyhole,
} from "lucide-react";

import { AlertNote } from "../../components/AlertNote";
import { PlanLimitDialog } from "../../components/PlanLimitDialog";
import { getPlanLimitError } from "../../utils/planLimits";
import { Button } from "../../components/Button";
import { ClientReadOnlyScope } from "../../components/ClientReadOnlyScope";
import { useAuth } from "../../components/contexts/AuthContext";
import { getData } from "../../components/hooks/getData";
import apiClient from "../../api-client";
import { mockDataConfig } from "../../theme/theme";
import { CompactClientCard } from "./CompactClientCard";
import { CompactClientCardSkeleton } from "./CompactClientCardSkeleton";
import { ClientModal } from "./ClientModal";
import { OnboardingFormSection } from "./OnboardingFormSection";
import { InvitationLinkSection } from "./InvitationLinkSection";
import { ClientUsersSection } from "./ClientUsersSection";
import SuccessPopup from "../../components/SuccessPopup";
import { EmptyStateMessage } from "../../components/EmptyStateMessage";
import { IncidentFiltersModal } from "../../components/IncidentFiltersModal";
import {
  DataTable,
  DataTableToolbar,
  toolbarViewModeButtonSx,
} from "../../components/DataTable";
import { ViewModeSelector } from "../../components/ViewModeSelector";
import { getClientTableColumns } from "./clientTableColumns";
import { canSeeContractedPack, getMaxClients } from "@/utils/agencyFeatures";
import { SeoConnectModal } from "./seoSetup/SeoConnectModal";
import { ClientCreatedModal } from "./seoSetup/ClientCreatedModal";
import { buildDateRangeChipLabel } from "../../utils/dateUtils";
import { refreshWelcomeProgress } from "../../Tutorials/welcomeProgress";

const packLabels = {
  ecommerce: "Pack E-commerce",
  basic: "Pack Basic",
  premium: "Pack Premium",
  local: "Pack Starter",
  infoproducto: "Pack Infoproducto",
  sin_pack: "Sin Pack",
};

const PACK_FILTER_OPTIONS = [
  "basic",
  "ecommerce",
  "premium",
  "local",
  "infoproducto",
].map((value) => ({ value, label: packLabels[value] }));

const CLIENTS_PAGE_SIZE = 40;
const CLIENTS_TABLE_DEFAULT_PAGE_SIZE = 25;

// La tabla es la vista por defecto; las tarjetas siguen disponibles desde el selector.
const CLIENTS_VIEW_MODE_OPTIONS = [
  { value: "table", label: "Tabla", icon: List },
  { value: "cards", label: "Tarjetas", icon: LayoutGrid },
];

const CLIENTS_SORT_FIELD_OPTIONS = [
  { value: "created_at", label: "Fecha de alta" },
  { value: "updated_at", label: "Fecha de última edición" },
];

const CLIENTS_SORT_DIRECTION_OPTIONS = [
  { value: "asc", label: "Ascendente" },
  { value: "desc", label: "Descendente" },
];

const createEmptyAdvancedFilters = () => ({
  pack: null,
  agency: null,
  consultant: null,
  archivedFrom: "",
  archivedTo: "",
  createdFrom: "",
  createdTo: "",
});

const cloneAdvancedFilters = (filters) => ({
  pack: filters.pack ? { ...filters.pack } : null,
  agency: filters.agency ? { ...filters.agency } : null,
  consultant: filters.consultant ? { ...filters.consultant } : null,
  archivedFrom: filters.archivedFrom || "",
  archivedTo: filters.archivedTo || "",
  createdFrom: filters.createdFrom || "",
  createdTo: filters.createdTo || "",
});

const normalizeApiList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.results)) return payload.results;
  return [];
};

export function Clients() {
  const { user: currentUser, interceptorReady } = useAuth();
  const theme = useMuiTheme();
  const [listViewMode, setListViewMode] = useState("table");
  // La tabla pagina por páginas (como el resto de tablas); las tarjetas usan scroll infinito
  const [tableRowsPerPage, setTableRowsPerPage] = useState(CLIENTS_TABLE_DEFAULT_PAGE_SIZE);
  // Se incrementa cada vez que se vacía la lista para forzar su re-sincronización
  const [clientsListResetKey, setClientsListResetKey] = useState(0);
  const {
    setPageContext,
    selectedFilter,
    selectedFilterData,
    changeSelectedFilter,
    userRole,
    refreshFilterData,
    clientsRefreshKey,
    filterRefresh,
    clientDetailRefreshKey,
    setClientDetailRefreshKey,
  } = useOutletContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const searchDebounceTimerRef = useRef(null);
  const lastSearchRef = useRef("");
  const [searchRefreshKey, setSearchRefreshKey] = useState(0);
  const [selectedClient, setSelectedClient] = useState(null);
  const [showNewClientModal, setShowNewClientModal] = useState(false);
  // Error de límite de plan pendiente de mostrar: { kind, planName, limit } o null
  const [planLimitError, setPlanLimitError] = useState(null);
  // Client whose SEO connect dialog is open: { id, name?, website? }.
  const [seoSetupClient, setSeoSetupClient] = useState(null);
  // Just-created client: the success modal offers to start its SEO setup.
  const [createdClient, setCreatedClient] = useState(null);
  const [agenciesRefresh, setagenciesRefresh] = useState(0);

  // The desktop welcome checklist can deep-link directly into the first real action.
  useEffect(() => {
    if (searchParams.get("welcome") !== "create-client") return;
    const next = new URLSearchParams(searchParams);
    next.delete("welcome");
    setSearchParams(next, { replace: true });
    if (["superadmin", "agency_admin"].includes(currentUser?.role)) {
      setShowNewClientModal(true);
      setagenciesRefresh((previous) => previous + 1);
    }
  }, [currentUser?.role, searchParams, setSearchParams]);

  // `?seoSetup=<client_id>` reopens it; the stepper draft resumes from localStorage.
  useEffect(() => {
    const seoSetupParam = searchParams.get("seoSetup");
    if (!seoSetupParam || !currentUser) return;
    const next = new URLSearchParams(searchParams);
    next.delete("seoSetup");
    setSearchParams(next, { replace: true });
    setSeoSetupClient({ id: Number(seoSetupParam) });
  }, [searchParams, currentUser, setSearchParams]);
  const [clientsUpdateKey, setClientsUpdateKey] = useState(0);
  const [refreshClients, setRefreshClients] = useState(0);
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    isError: false,
  }); // Ref para acceder a las funciones de OnboardingFormSection
  const onboardingFormRef = useRef(null);
  const loadMoreRef = useRef(null);

  // Track previous filter to detect changes
  const [previousFilter, setPreviousFilter] = useState(selectedFilter);
  const [archivedFilter, setArchivedFilter] = useState("active");
  const [archiveStats, setArchiveStats] = useState({
    active: 0,
    archived: 0,
    all: 0,
  });
  const [filterRefreshKey, setFilterRefreshKey] = useState(0);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [sortField, setSortField] = useState("created_at");
  const [sortDirection, setSortDirection] = useState("desc");
  const [sortRefreshKey, setSortRefreshKey] = useState(0);
  const [appliedAdvancedFilters, setAppliedAdvancedFilters] = useState(() =>
    createEmptyAdvancedFilters()
  );
  const [draftAdvancedFilters, setDraftAdvancedFilters] = useState(() =>
    createEmptyAdvancedFilters()
  );

  const packFilterValue = appliedAdvancedFilters.pack?.value || null;
  const agencyFilterValue = appliedAdvancedFilters.agency?.value || null;
  const consultantFilterValue = appliedAdvancedFilters.consultant?.value || null;
  const archivedFromFilterValue = appliedAdvancedFilters.archivedFrom || "";
  const archivedToFilterValue = appliedAdvancedFilters.archivedTo || "";
  const createdFromFilterValue = appliedAdvancedFilters.createdFrom || "";
  const createdToFilterValue = appliedAdvancedFilters.createdTo || "";
  const canUseArchivedAdvancedFilters =
    currentUser?.role === "superadmin" || currentUser?.role === "agency_admin";

  const orderingValue = React.useMemo(
    () => (sortDirection === "desc" ? `-${sortField}` : sortField),
    [sortField, sortDirection]
  );

  const handleSortFieldChange = (field) => {
    setSortField(field);
    setSortRefreshKey((currentKey) => currentKey + 1);
  };

  const handleSortDirectionChange = (direction) => {
    setSortDirection(direction);
    setSortRefreshKey((currentKey) => currentKey + 1);
  };

  // Get URL params for view state
  const viewMode = searchParams.get("view");
  const viewClientId = searchParams.get("clientId")
    ? parseInt(searchParams.get("clientId"))
    : null;
  const detailViewModes = ["detail", "onboarding", "invitation"];
  const isDetailView = viewMode ? detailViewModes.includes(viewMode) : false;
  const isTableView = !isDetailView && listViewMode === "table";
  const currentClientsPageSize = isTableView ? tableRowsPerPage : CLIENTS_PAGE_SIZE;
  // Debounce para el buscador inline
  useEffect(() => {
    if (searchDebounceTimerRef.current) {
      clearTimeout(searchDebounceTimerRef.current);
    }

    searchDebounceTimerRef.current = setTimeout(() => {
      const trimmed = searchInput.trim();

      // CASO 1: transición búsqueda → vacío (forzar reset)
      if (trimmed === '' && lastSearchRef.current !== '') {
        lastSearchRef.current = '';
        setSearchTerm('');
        setSearchRefreshKey(prev => prev + 1);
        return;
      }

      // CASO 2: búsqueda normal (mínimo 2 caracteres)
      if (trimmed.length >= 2 && trimmed !== lastSearchRef.current) {
        lastSearchRef.current = trimmed;
        setSearchTerm(trimmed);
      }
    }, 300);

    return () => {
      if (searchDebounceTimerRef.current) {
        clearTimeout(searchDebounceTimerRef.current);
      }
    };
  }, [searchInput]);

  // Effect to reset to list view when filter changes while in detail view
  useEffect(() => {
    if (previousFilter !== selectedFilter && isDetailView) {
      setSearchParams({});
    }
    setPreviousFilter(selectedFilter);
  }, [selectedFilter, isDetailView, previousFilter]);

  const getHeaderInfo = () => {
    switch (currentUser.role) {
      case "superadmin":
        return {
          title: "Gestión de Clientes",
          subtitle: selectedFilter
            ? "Vista de clientes de la agencia seleccionada"
            : "Vista completa de todos los clientes de la agencia",
        };
      case "agency_admin":
        return {
          title: "Clientes",
          subtitle: selectedFilter
            ? "Vista del cliente seleccionado"
            : "Gestiona todos los clientes de tu agencia",
        };
      case "consultant":
        return {
          title: "Mis Clientes",
          subtitle: selectedFilter
            ? "Vista del cliente seleccionado"
            : "Clientes que tienes asignados",
        };
      default:
        return {
          title: "Clientes",
          subtitle: "Lista de clientes",
        };
    }
  };

  const headerInfo = getHeaderInfo();

  const archivedQueryValue = React.useMemo(() => {
    switch (archivedFilter) {
      case "archived":
        return "true";
      case "all":
        return "all";
      default:
        return "false";
    }
  }, [archivedFilter]);

  const globalAgencyOption = React.useMemo(() => {
    if (
      currentUser?.role !== "superadmin" ||
      !selectedFilter ||
      !selectedFilterData?.name
    ) {
      return null;
    }

    return {
      value: selectedFilter,
      label: selectedFilterData.name,
    };
  }, [currentUser?.role, selectedFilter, selectedFilterData]);

  const effectiveAgencyFilterValue = React.useMemo(() => {
    if (currentUser?.role !== "superadmin") return null;
    return globalAgencyOption?.value || agencyFilterValue || null;
  }, [currentUser?.role, globalAgencyOption, agencyFilterValue]);

  const buildListEndpoint = React.useCallback(
    (page) => {
      const queryParams = new URLSearchParams({
        expand: "consultants",
        archived: archivedQueryValue,
        page: page.toString(),
        page_size: currentClientsPageSize.toString(),
      });

      if (packFilterValue) {
        queryParams.set("setup_variant", packFilterValue);
      }

      queryParams.set("ordering", orderingValue);

      if (searchTerm) {
        queryParams.set("search", searchTerm);
      }

      if (createdFromFilterValue) {
        queryParams.set("created_from", createdFromFilterValue);
      }
      if (createdToFilterValue) {
        queryParams.set("created_to", createdToFilterValue);
      }

      if (canUseArchivedAdvancedFilters) {
        if (effectiveAgencyFilterValue) {
          queryParams.set("agency", effectiveAgencyFilterValue);
        }

        if (consultantFilterValue) {
          queryParams.set("consultant", consultantFilterValue);
        }

        if (archivedQueryValue !== "false") {
          if (archivedFromFilterValue) {
            queryParams.set("archived_from", archivedFromFilterValue);
          }
          if (archivedToFilterValue) {
            queryParams.set("archived_to", archivedToFilterValue);
          }
        }
      }

      return queryParams.toString();
    },
    [
      currentClientsPageSize,
      archivedFromFilterValue,
      archivedQueryValue,
      archivedToFilterValue,
      canUseArchivedAdvancedFilters,
      consultantFilterValue,
      createdFromFilterValue,
      createdToFilterValue,
      currentUser?.role,
      effectiveAgencyFilterValue,
      orderingValue,
      packFilterValue,
      searchTerm,
    ]
  );

  const buildDetailEndpoint = React.useCallback((clientId) => {
    return `clients/${clientId}/`;
  }, []);

  // Definir endpoint para cargar todos los consultores disponibles según el rol
  const getConsultantsEndpoint = () => {
    if (currentUser?.role === "superadmin" && !selectedFilter) {
      return "users/?role=consultant";
    } else if (currentUser?.role === "superadmin" && selectedFilter) {
      return `users/?role=consultant&agency=${selectedFilter}`;
    } else if (
      currentUser?.role === "agency_admin" ||
      currentUser?.role === "consultant"
    ) {
      return `users/?role=consultant`; // Backend filters by user's agency automatically
    }
    return null;
  };

  // Determinar si debemos hacer fetch de datos
  // Para agency_admin y consultant con selectedFilter, no hacer fetch cuando archivedFilter es 'archived'
  const shouldFetchClients = React.useMemo(() => {
    if (!interceptorReady || !currentUser) return false;

    // Para agency_admin y consultant con selectedFilter activo
    if (
      (currentUser.role === "agency_admin" ||
        currentUser.role === "consultant") &&
      selectedFilter
    ) {
      // No hacer fetch solo si el filtro es 'archived' (los filtros solo muestran clientes activos)
      return archivedFilter !== "archived";
    }

    // Para otros casos, siempre hacer fetch
    return true;
  }, [interceptorReady, currentUser, selectedFilter, archivedFilter]);

  // Cargar clientes usando getData
  const [clientsPage, setClientsPage] = useState(1);
  const [clientsList, setClientsList] = useState([]);
  const [hasMoreClients, setHasMoreClients] = useState(true);
  const [totalClients, setTotalClients] = useState(null);

  const clientsListEndpoint = React.useMemo(() => {
    if (!shouldFetchClients || isDetailView) return null;

    const queryParams = new URLSearchParams(buildListEndpoint(clientsPage));

    // Filtrar por cliente específico para agency_admin y consultant
    if ((currentUser?.role === "agency_admin" || currentUser?.role === "consultant") && selectedFilter) {
      queryParams.set("client_id", selectedFilter);
    }

    return `clients/?${queryParams.toString()}`;
  }, [
    shouldFetchClients,
    isDetailView,
    buildListEndpoint,
    clientsPage,
    currentUser?.role,
    selectedFilter,
  ]);

  const {
    data: clientsPageData,
    error: clientsListError,
    isLoading: clientsListIsLoading,
    isFetching: clientsListIsFetching,
    isInitialLoading: clientsListIsInitialLoading,
  } = getData(
    clientsListEndpoint,
    shouldFetchClients && !isDetailView,
    [
      refreshClients,
      currentUser?.id,
      currentUser?.agencyId,
      clientsRefreshKey,
      filterRefresh,
      archivedFilter,
      filterRefreshKey,
      clientsUpdateKey,
      clientsListEndpoint,
      searchRefreshKey,
      sortRefreshKey,
    ]
  );


  const clientDetailEndpoint = React.useMemo(() => {
    if (!isDetailView || !viewClientId) return null;
    return buildDetailEndpoint(viewClientId);
  }, [isDetailView, viewClientId, buildDetailEndpoint]);

  const shouldFetchClientDetail =
    interceptorReady && !!clientDetailEndpoint;

  const {
    data: clientDetailData,
    error: clientDetailError,
    isLoading: clientDetailIsLoading,
    isInitialLoading: clientDetailIsInitialLoading,
  } = getData(
    clientDetailEndpoint,
    shouldFetchClientDetail,
    [
      viewClientId,
      clientDetailRefreshKey,
      clientDetailEndpoint,
    ]
  );

  // Cargar todos los consultores (solo para superadmin y agency_admin)
  // Para agency_admin y consultant con selectedFilter, usar el mismo shouldFetchClients
  const shouldFetchConsultants = React.useMemo(() => {
    if (!interceptorReady || !currentUser) return false;
    if (
      !(
        currentUser?.role === "superadmin" ||
        currentUser?.role === "agency_admin" ||
        currentUser?.role === "consultant"
      )
    ) {
      return false;
    }

    // Para agency_admin y consultant con selectedFilter activo
    if (
      (currentUser.role === "agency_admin" ||
        currentUser.role === "consultant") &&
      selectedFilter
    ) {
      // No hacer fetch solo si el filtro es 'archived'
      return archivedFilter !== "archived";
    }

    return true;
  }, [interceptorReady, currentUser, selectedFilter, archivedFilter]);

  const { data: consultantsData } = getData(
    getConsultantsEndpoint(),
    shouldFetchConsultants,
    [
      currentUser?.id,
      currentUser?.agencyId,
      refreshClients,
      clientsRefreshKey,
      filterRefresh,
      filterRefreshKey,
    ]
  );

  // Límite de clientes por agencia: solo cuentan los no archivados, igual que el backend
  const maxClients =
    currentUser?.role === "agency_admin" ? getMaxClients(currentUser) : null;
  const { data: clientLimitData } = getData(
    "clients/?page_size=1",
    interceptorReady && maxClients !== null,
    [currentUser?.id, currentUser?.agencyId, refreshClients, clientsRefreshKey]
  );
  const agencyClientCount = clientLimitData?.archive_stats?.active ?? null;
  const clientLimitReached =
    maxClients !== null &&
    agencyClientCount !== null &&
    agencyClientCount >= maxClients;

  const shouldFetchFilterAgencies =
    interceptorReady &&
    isFilterModalOpen &&
    currentUser?.role === "superadmin" &&
    !globalAgencyOption;

  const {
    data: filterAgenciesData,
    isLoading: isLoadingFilterAgencies,
  } = getData(
    "agencies/",
    shouldFetchFilterAgencies,
    [currentUser?.id, filterRefresh, filterRefreshKey]
  );

  // Manejar datos según si es un solo cliente o una lista
  const clients = React.useMemo(() => {
    // Si no debemos hacer fetch (agency_admin/consultant con selectedFilter y archivedFilter === 'archived')
    // retornar array vacío para mostrar el mensaje de "no hay clientes archivados"
    if (
      (currentUser?.role === "agency_admin" ||
        currentUser?.role === "consultant") &&
      selectedFilter &&
      archivedFilter === "archived"
    ) {
      return [];
    }

    return clientsList;
  }, [
    clientsList,
    selectedFilter,
    currentUser?.role,
    archivedFilter,
  ]);

  // Update archive stats from backend response
  useEffect(() => {
    // Re-sincroniza también cuando se vuelve de la vista detalle a la lista:
    // si los filtros no cambiaron, React Query devuelve la respuesta cacheada
    // (misma referencia) sin disparar una nueva petición, y este efecto no se
    // re-ejecutaría por dependencias si no incluyésemos isDetailView aquí.
    // Lo mismo ocurre al volver a una vista (tabla ↔ tarjetas) cuya página ya
    // está en caché: el efecto de reset de abajo vacía la lista después de este
    // efecto, así que clientsListResetKey fuerza una segunda sincronización.
    if (isDetailView || !clientsPageData) return;

    if (Array.isArray(clientsPageData)) {
      setClientsList(clientsPageData);
      setHasMoreClients(false);
      setTotalClients(clientsPageData.length);
      return;
    }

    const results = Array.isArray(clientsPageData?.results)
      ? clientsPageData.results
      : [];
    const totalCount =
      typeof clientsPageData?.count === "number" ? clientsPageData.count : null;

    setClientsList((prev) => {
      if (isTableView || clientsPage === 1) {
        return results;
      }

      const seen = new Set(prev.map((client) => client.id));
      const merged = results.filter((client) => !seen.has(client.id));
      return [...prev, ...merged];
    });
    if (totalCount !== null) {
      const totalPages = Math.ceil(totalCount / currentClientsPageSize);
      setHasMoreClients(clientsPage < totalPages);
      setTotalClients(totalCount);
    } else {
      setHasMoreClients(Boolean(clientsPageData?.next));
      setTotalClients(null);
    }

    if (clientsPageData?.archive_stats) {
      setArchiveStats({
        active: clientsPageData.archive_stats.active || 0,
        archived: clientsPageData.archive_stats.archived || 0,
        all: clientsPageData.archive_stats.all || 0,
      });
    }
  }, [
    clientsPageData,
    clientsPage,
    currentClientsPageSize,
    isDetailView,
    isTableView,
    clientsListResetKey,
  ]);

  useEffect(() => {
    if (isDetailView || !shouldFetchClients) {
      setClientsPage(1);
      setClientsList([]);
      setHasMoreClients(true);
      setTotalClients(null);
      return;
    }

    setClientsPage(1);
    setClientsList([]);
    setHasMoreClients(true);
    setTotalClients(null);
    setClientsListResetKey((key) => key + 1);
  }, [
    archivedFilter,
    packFilterValue,
    searchTerm,
    selectedFilter,
    refreshClients,
    clientsRefreshKey,
    filterRefresh,
    filterRefreshKey,
    clientsUpdateKey,
    shouldFetchClients,
    orderingValue,
    listViewMode,
    tableRowsPerPage,
  ]);

  // Procesar consultores disponibles (solo para superadmin y agency_admin)
  const availableConsultants = React.useMemo(() => {
    if (
      (currentUser?.role !== "superadmin" &&
        currentUser?.role !== "agency_admin" &&
        currentUser?.role !== "consultant") ||
      !consultantsData
    )
      return [];

    return consultantsData?.results || consultantsData || [];
  }, [consultantsData, currentUser?.role]);

  const agencyOptions = React.useMemo(() => {
    if (currentUser?.role !== "superadmin") return [];

    return normalizeApiList(filterAgenciesData).map((agency) => ({
      value: agency.id,
      label: agency.name,
    }));
  }, [currentUser?.role, filterAgenciesData]);

  const selectedAgencyForConsultantOptions = React.useMemo(() => {
    if (currentUser?.role === "agency_admin") {
      return currentUser?.agencyId || null;
    }

    if (currentUser?.role !== "superadmin") return null;

    if (globalAgencyOption?.value) {
      return globalAgencyOption.value;
    }

    return isFilterModalOpen
      ? draftAdvancedFilters.agency?.value || null
      : agencyFilterValue || null;
  }, [
    agencyFilterValue,
    currentUser?.agencyId,
    currentUser?.role,
    draftAdvancedFilters.agency,
    globalAgencyOption,
    isFilterModalOpen,
  ]);

  const consultantOptions = React.useMemo(() => {
    if (!canUseArchivedAdvancedFilters) return [];

    const normalizedAgencyId = selectedAgencyForConsultantOptions
      ? selectedAgencyForConsultantOptions.toString()
      : null;

    return availableConsultants
      .filter((consultant) => {
        if (!normalizedAgencyId) return true;

        const primaryAgencyId =
          (
            consultant.agency_id ??
            (typeof consultant.agency === "object"
              ? consultant.agency?.id
              : consultant.agency) ??
            null
          )?.toString?.() ||
          null;

        if (primaryAgencyId === normalizedAgencyId) {
          return true;
        }

        if (!Array.isArray(consultant.invited_agencies)) {
          return false;
        }

        return consultant.invited_agencies.some(
          (agency) => agency?.id?.toString?.() === normalizedAgencyId
        );
      })
      .map((consultant) => {
        const fullName =
          consultant.full_name ||
          `${consultant.first_name || ""} ${consultant.last_name || ""}`.trim() ||
          consultant.email ||
          `Consultor ${consultant.id}`;
        const label =
          normalizedAgencyId || !consultant.agency_name
            ? fullName
            : `${fullName} - ${consultant.agency_name}`;

        return {
          value: consultant.id,
          label,
        };
      })
      .sort((left, right) => left.label.localeCompare(right.label, "es"));
  }, [
    availableConsultants,
    canUseArchivedAdvancedFilters,
    selectedAgencyForConsultantOptions,
  ]);

  const isClientsLoading = isDetailView
    ? !clientDetailData && (clientDetailIsLoading || clientDetailIsInitialLoading)
    : clientsList.length === 0 &&
    (clientsListIsLoading ||
      clientsListIsInitialLoading ||
      clientsListIsFetching);

  const hasPendingClientsListSync =
    !isDetailView &&
    clientsPage === 1 &&
    clientsList.length === 0 &&
    ((Array.isArray(clientsPageData) && clientsPageData.length > 0) ||
      (Array.isArray(clientsPageData?.results) &&
        clientsPageData.results.length > 0));

  const showClientsSkeleton = isClientsLoading || hasPendingClientsListSync;

  const isLoadingMoreClients =
    !isDetailView &&
    clientsPage > 1 &&
    clientsListIsFetching;

  useEffect(() => {
    if (isDetailView || isTableView || !hasMoreClients) return;
    if (clientsList.length === 0) return;
    if (!loadMoreRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !clientsListIsFetching) {
          setClientsPage((prev) => {
            if (!hasMoreClients) return prev;
            if (typeof totalClients === "number" && totalClients > 0) {
              const totalPages = Math.ceil(totalClients / currentClientsPageSize);
              if (prev >= totalPages) return prev;
            }
            return prev + 1;
          });
        }
      },
      { rootMargin: "200px 0px" }
    );

    observer.observe(loadMoreRef.current);
    return () => observer.disconnect();
  }, [
    isDetailView,
    isTableView,
    hasMoreClients,
    clientsListIsFetching,
    clientsList.length,
    totalClients,
    currentClientsPageSize,
  ]);

  const handleTablePageChange = React.useCallback((newPage) => {
    setClientsPage(newPage + 1);
  }, []);

  const handleTableRowsPerPageChange = React.useCallback((nextRowsPerPage) => {
    setTableRowsPerPage(nextRowsPerPage);
  }, []);

  // Función para refrescar solo clientes y consultores (sin agencias)
  const loadClients = React.useCallback(() => {
    setRefreshClients((prev) => prev + 1);
  }, [setRefreshClients]);

  // Handle view mode changes
  const handleViewOnboarding = React.useCallback(
    (clientId) => {
      setClientDetailRefreshKey((prev) => prev + 1);
      setSearchParams({
        view: "detail",
        clientId: clientId.toString(),
      });
    },
    [setClientDetailRefreshKey, setSearchParams]
  );

  const handleClientSuccess = React.useCallback((message) => {
    setNotification({ open: true, message, isError: false });
  }, [setNotification]);

  const handleClientError = React.useCallback((message) => {
    setNotification({ open: true, message, isError: true });
  }, [setNotification]);

  const handleBackToList = async () => {
    // Increment key to refresh list
    setClientsUpdateKey((prev) => prev + 1);
    setRefreshClients((prev) => prev + 1);
    // Navegar de vuelta a la lista
    setSearchParams({});
  };

  // Find the client being viewed
  const viewedClient = viewClientId
    ? clientDetailData ||
    clients.find((c) => c.id === viewClientId)
    : null;
  // Nombre provisional mientras el detalle del cliente aún está cargando,
  // para no repintar la cabecera de lista durante la transición.
  const viewedClientName = viewedClient?.name || "";

  // Set page context
  useEffect(() => {
    if (currentUser) {
      // Adjust title and subtitle based on view mode
      let title = headerInfo.title;
      let subtitle = headerInfo.subtitle;
      let leftContent = null;
      let breadcrumbItems = null;

      // Generar breadcrumb según el rol y la vista
      if (currentUser.role === "superadmin") {
        if (isDetailView) {
          // Vista detalle: Vista Global > Agencia X > Cliente X (con filtro) o Vista Global > Cliente X (sin filtro)
          if (selectedFilter && selectedFilterData) {
            breadcrumbItems = [
              {
                label: "Vista Global",
                onClick: () => changeSelectedFilter("global"),
              },
              {
                label: `Agencia: ${selectedFilterData.name}`,
                onClick: () => handleBackToList(),
              },
              { label: `Cliente: ${viewedClientName}` },
            ];
          } else {
            breadcrumbItems = [
              { label: "Vista Global", onClick: () => handleBackToList() },
              { label: `Cliente: ${viewedClientName}` },
            ];
          }
        } else {
          // Vista lista: Vista Global > Agencia X (con filtro) o solo Vista Global (sin filtro)
          if (selectedFilter && selectedFilterData) {
            breadcrumbItems = [
              {
                label: "Vista Global",
                onClick: () => changeSelectedFilter("global"),
              },
              { label: `Agencia: ${selectedFilterData.name}` },
            ];
          } else {
            breadcrumbItems = [{ label: "Vista Global" }];
          }
        }
      } else if (
        currentUser.role === "agency_admin" ||
        currentUser.role === "consultant"
      ) {
        if (isDetailView) {
          // Vista detalle: Vista Global > Cliente X
          if (selectedFilter && selectedFilterData) {
            breadcrumbItems = [
              {
                label: "Vista Global",
                onClick: () => changeSelectedFilter("global"),
              },
              { label: `Cliente: ${selectedFilterData.name}` },
            ];
          } else {
            breadcrumbItems = [
              { label: "Vista Global", onClick: () => handleBackToList() },
              { label: `Cliente: ${viewedClientName}` },
            ];
          }
        } else {
          // Vista lista: Vista Global > Cliente X (con filtro) o solo Vista Global (sin filtro)
          if (selectedFilter && selectedFilterData) {
            breadcrumbItems = [
              {
                label: "Vista Global",
                onClick: () => changeSelectedFilter("global"),
              },
              { label: `Cliente: ${selectedFilterData.name}` },
            ];
          } else {
            breadcrumbItems = [{ label: "Vista Global" }];
          }
        }
      }

      if (isDetailView) {
        leftContent = (
          <IconButton
            onClick={handleBackToList}
            size="small"
            sx={{
              color: "primary.main",
              padding: "4px",
              "&:hover": {
                backgroundColor: theme.palette.action.hover,
              },
            }}
            disableRipple={false}
          >
            <ChevronLeft size={20} />
          </IconButton>
        );
        title = (
          <>
            Detalles del Cliente:{"  "}
            <span
              style={{ color: theme.palette.text.secondary, marginLeft: "4px" }}
            >
              {viewedClientName}
            </span>
          </>
        );
        subtitle = "Información general y enlace de invitación";
      }

      const rightContent = !isDetailView ? (
        // Show new client button in list view
        <Stack direction="row" spacing={2} alignItems="center">
          {(currentUser?.role === "superadmin" ||
            currentUser?.role === "agency_admin") && (
              <Button
                onClick={() => {
                  // Con el límite alcanzado el botón vende el upgrade en vez de deshabilitarse
                  if (clientLimitReached) {
                    setPlanLimitError({ kind: "clients", limit: maxClients });
                    return;
                  }
                  setShowNewClientModal(true);
                  setagenciesRefresh((prev) => prev + 1);
                }}
              >
                <UserPlus size={16} />
                Nuevo Cliente
              </Button>
            )}
        </Stack>
      ) : null;

      setPageContext({
        title,
        subtitle,
        showTitle: true,
        leftContent: leftContent,
        rightContent: rightContent,
        breadcrumbItems,
      });
    }
  }, [
    currentUser,
    setPageContext,
    headerInfo.title,
    headerInfo.subtitle,
    searchTerm,
    selectedFilter,
    selectedFilterData,
    viewMode,
    isDetailView,
    viewedClient,
    viewedClientName,
    theme,
    clientLimitReached,
    maxClients,
  ]);

  const filteredClients = React.useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    const normalizedSelectedFilter = selectedFilter
      ? selectedFilter.toString()
      : null;

    return clients.filter((client) => {
      const clientName = (client.name || "").toLowerCase();
      const matchesSearch =
        !normalizedSearch || clientName.includes(normalizedSearch);

      // El filtrado de archivados se hace en la API mediante el parámetro 'archived'
      // No es necesario filtrar aquí localmente

      const matchesSelected = (() => {
        if (!normalizedSelectedFilter) return true;

        if (currentUser?.role === "superadmin") {
          const agencyValue =
            client.agency_id ??
            (typeof client.agency === "object"
              ? client.agency?.id
              : client.agency) ??
            client.agencyId ??
            null;
          if (!agencyValue) return false;
          return agencyValue.toString() === normalizedSelectedFilter;
        }

        if (
          currentUser?.role === "agency_admin" ||
          currentUser?.role === "consultant"
        ) {
          return client.id && client.id.toString() === normalizedSelectedFilter;
        }

        return true;
      })();

      return matchesSearch && matchesSelected;
    });
  }, [clients, searchTerm, selectedFilter, currentUser?.role]);

  const advancedFilterChips = React.useMemo(() => {
    const chips = [];

    if (appliedAdvancedFilters.pack) {
      chips.push({
        key: "pack",
        label: `Pack: ${appliedAdvancedFilters.pack.label}`,
      });
    }

    if (
      currentUser?.role === "superadmin" &&
      appliedAdvancedFilters.agency &&
      !globalAgencyOption
    ) {
      chips.push({
        key: "agency",
        label: `Agencia: ${appliedAdvancedFilters.agency.label}`,
      });
    }

    if (canUseArchivedAdvancedFilters && appliedAdvancedFilters.consultant) {
      chips.push({
        key: "consultant",
        label: `Consultor: ${appliedAdvancedFilters.consultant.label}`,
      });
    }

    if (
      canUseArchivedAdvancedFilters &&
      (appliedAdvancedFilters.archivedFrom || appliedAdvancedFilters.archivedTo)
    ) {
      chips.push({
        key: "archivedDateRange",
        label: buildDateRangeChipLabel({
          label: "Baja",
          start: appliedAdvancedFilters.archivedFrom,
          end: appliedAdvancedFilters.archivedTo,
        }),
      });
    }

    if (appliedAdvancedFilters.createdFrom || appliedAdvancedFilters.createdTo) {
      chips.push({
        key: "createdDateRange",
        label: buildDateRangeChipLabel({
          label: "Alta",
          start: appliedAdvancedFilters.createdFrom,
          end: appliedAdvancedFilters.createdTo,
        }),
      });
    }

    return chips;
  }, [appliedAdvancedFilters, canUseArchivedAdvancedFilters, currentUser?.role, globalAgencyOption]);

  const handleOpenFilterModal = () => {
    setDraftAdvancedFilters(cloneAdvancedFilters(appliedAdvancedFilters));
    setIsFilterModalOpen(true);
  };

  const handleCloseFilterModal = () => {
    setIsFilterModalOpen(false);
    setDraftAdvancedFilters(cloneAdvancedFilters(appliedAdvancedFilters));
  };

  const handleDraftFilterChange = (field, value) => {
    setDraftAdvancedFilters((prev) => ({
      ...prev,
      [field]: value,
      ...(field === "agency" ? { consultant: null } : {}),
    }));
  };

  const handleClearDraftFilters = () => {
    setDraftAdvancedFilters(createEmptyAdvancedFilters());
  };

  const handleApplyAdvancedFilters = () => {
    const nextFilters = cloneAdvancedFilters(draftAdvancedFilters);
    setAppliedAdvancedFilters(nextFilters);
    if (
      canUseArchivedAdvancedFilters &&
      archivedFilter !== "archived" &&
      (nextFilters.archivedFrom || nextFilters.archivedTo)
    ) {
      setArchivedFilter("archived");
    }
    setFilterRefreshKey((prev) => prev + 1);
    setIsFilterModalOpen(false);
  };

  const handleRemoveAdvancedFilter = React.useCallback((key) => {
    const isArchivedDateRange =
      key === "archivedDateRange" || key === "archivedFrom" || key === "archivedTo";
    const isCreatedDateRange =
      key === "createdDateRange" || key === "createdFrom" || key === "createdTo";

    setAppliedAdvancedFilters((prev) => {
      const next = cloneAdvancedFilters(prev);
      if (isArchivedDateRange) {
        next.archivedFrom = "";
        next.archivedTo = "";
      } else if (isCreatedDateRange) {
        next.createdFrom = "";
        next.createdTo = "";
      } else {
        next[key] = null;
      }
      return next;
    });
    setDraftAdvancedFilters((prev) => {
      const next = cloneAdvancedFilters(prev);
      if (isArchivedDateRange) {
        next.archivedFrom = "";
        next.archivedTo = "";
      } else if (isCreatedDateRange) {
        next.createdFrom = "";
        next.createdTo = "";
      } else {
        next[key] = null;
      }
      if (key === "agency") {
        next.consultant = null;
      }
      return next;
    });
    setFilterRefreshKey((prev) => prev + 1);
  }, []);

  const clearArchivedDateFilters = React.useCallback(() => {
    setAppliedAdvancedFilters((prev) => {
      if (!prev.archivedFrom && !prev.archivedTo) {
        return prev;
      }

      return {
        ...cloneAdvancedFilters(prev),
        archivedFrom: "",
        archivedTo: "",
      };
    });

    setDraftAdvancedFilters((prev) => {
      if (!prev.archivedFrom && !prev.archivedTo) {
        return prev;
      }

      return {
        ...cloneAdvancedFilters(prev),
        archivedFrom: "",
        archivedTo: "",
      };
    });
  }, []);

  const filterConfigs = React.useMemo(
    () => {
      const configs = [];

      if (currentUser?.role === "superadmin") {
        configs.push(
          {
            key: "agency",
            title: "Agencia",
            icon: Building2,
            options: agencyOptions,
            loading: isLoadingFilterAgencies,
            placeholder: "Selecciona una agencia",
            disabled: Boolean(globalAgencyOption),
            fallbackOption: globalAgencyOption,
          }
        );
      }

      if (canUseArchivedAdvancedFilters) {
        configs.push(
          {
            key: "consultant",
            title: "Consultor",
            icon: UserRound,
            options: consultantOptions,
            placeholder: "Selecciona un consultor",
          }
        );
      }

      if (canSeeContractedPack(currentUser)) {
        configs.push(
          {
            key: "pack",
            title: "Pack contratado",
            icon: Package,
            options: PACK_FILTER_OPTIONS,
            placeholder: "Selecciona un pack",
            searchable: false,
          }
        );
      }

      configs.push({
        key: "createdDateRange",
        type: "date-range",
        ui: "calendar-range",
        title: "Fecha de alta",
        description: "Acota los clientes dados de alta dentro de un periodo concreto.",
        icon: Calendar,
        startKey: "createdFrom",
        endKey: "createdTo",
      });

      if (canUseArchivedAdvancedFilters) {
        configs.push({
          key: "archivedDateRange",
          type: "date-range",
          ui: "calendar-range",
          title: "Fecha de baja",
          description: "Acota los clientes archivados dentro de un periodo concreto.",
          icon: Calendar,
          startKey: "archivedFrom",
          endKey: "archivedTo",
        });
      }

      return configs;
    },
    [
      agencyOptions,
      canUseArchivedAdvancedFilters,
      consultantOptions,
      currentUser,
      globalAgencyOption,
      isLoadingFilterAgencies,
    ]
  );

  const handleArchivedFilterChange = (value) => {
    if (archivedFilter === value) return;

    const shouldClearArchivedDateFilters =
      value !== "archived" &&
      (appliedAdvancedFilters.archivedFrom || appliedAdvancedFilters.archivedTo);

    if (shouldClearArchivedDateFilters) {
      clearArchivedDateFilters();
    }

    setArchivedFilter(value);
    setClientsPage(1);
    setClientsList([]);
    setHasMoreClients(true);
    setTotalClients(null);
    setRefreshClients((prev) => prev + 1);
    if (shouldClearArchivedDateFilters) {
      setFilterRefreshKey((prev) => prev + 1);
    }
  };

  // Toolbar común: integrado en la tabla o como card propia en la vista de tarjetas.
  const clientsToolbarProps = {
    searchValue: searchInput,
    onSearchChange: setSearchInput,
    searchPlaceholder: "Buscar clientes...",
    tabs: [
      { value: "active", label: "Activos", count: archiveStats.active },
      { value: "archived", label: "Archivados", count: archiveStats.archived },
      { value: "all", label: "Todos", count: archiveStats.all },
    ],
    activeTab: archivedFilter,
    onTabChange: handleArchivedFilterChange,
    filterChips: advancedFilterChips.map((chip) => ({
      key: `${chip.key}-${chip.label}`,
      label: chip.label,
      onRemove: () => handleRemoveAdvancedFilter(chip.key),
    })),
    sortFieldOptions: CLIENTS_SORT_FIELD_OPTIONS,
    sortField,
    sortDirection,
    sortDirectionOptions: CLIENTS_SORT_DIRECTION_OPTIONS,
    onSortFieldChange: handleSortFieldChange,
    onSortDirectionChange: handleSortDirectionChange,
    onFilterClick: handleOpenFilterModal,
    activeFiltersCount: advancedFilterChips.length,
    filterTooltip: "Editar filtros",
    toolbarEnd: (
      <ViewModeSelector
        options={CLIENTS_VIEW_MODE_OPTIONS}
        value={listViewMode}
        onChange={setListViewMode}
        tooltipTitle="Escoger vista"
        buttonSx={toolbarViewModeButtonSx}
      />
    ),
  };

  const clientTableColumns = getClientTableColumns({
    showAgency: currentUser?.role === "superadmin" && !selectedFilter,
    showArchivedDate: archivedFilter === "archived",
    showPack: canSeeContractedPack(currentUser),
  });

  const clientCardProps = {
    archivedFilter,
    setSelectedClient,
    onClientDeleted: loadClients,
    onClientUpdated: loadClients,
    availableConsultants,
    onSuccess: handleClientSuccess,
    onError: handleClientError,
    onViewOnboarding: handleViewOnboarding,
    packLabels,
  };

  const emptyStateMessage = React.useMemo(() => {
    switch (archivedFilter) {
      case "archived":
        return "No hay clientes archivados";
      case "active":
        return "No hay clientes activos";
      case "all":
        return "Aún no hay clientes";
      default:
        return "Aún no hay clientes";
    }
  }, [archivedFilter]);

  const handleNewClientSave = async (newClientData) => {
    try {
      // Crear el cliente usando la API
      const response = await apiClient.post("clients/", newClientData);
      refreshWelcomeProgress();

      // Si hay consultores asignados, asignarlos al cliente
      if (
        newClientData.consultoresAsignados &&
        newClientData.consultoresAsignados.length > 0
      ) {
        try {
          await apiClient.post(
            `clients/${response.data.id}/assign-consultants/`,
            {
              consultant_ids: newClientData.consultoresAsignados,
            }
          );
        } catch (assignError) {
          console.error("Error asignando consultores:", assignError);
        }
      }

      // Refrescar la lista de clientes
      loadClients();

      // Cerrar el modal
      setShowNewClientModal(false);

      if (response.data?.id) {
        setCreatedClient(response.data);
      } else {
        setNotification({ open: true, message: "Cliente creado exitosamente", isError: false });
      }

      // Refrescar datos del filtro en AppLayout (solo para agency_admin y consultant)
      if (
        refreshFilterData &&
        (currentUser.role === "agency_admin" ||
          currentUser.role === "consultant")
      ) {
        refreshFilterData();
      }

      return response.data;
    } catch (error) {
      console.error("Error creando cliente:", error);

      const limitError = getPlanLimitError(error);
      if (limitError?.kind === "clients") {
        setShowNewClientModal(false);
        setPlanLimitError(limitError);
        throw error;
      }

      // Extraer mensaje de error del backend
      let errorMessage = "Error al crear el cliente";
      if (error.response?.data) {
        const errorData = error.response.data;
        // Si hay errores de validación específicos
        if (typeof errorData === "object") {
          const firstError = Object.values(errorData)[0];
          if (Array.isArray(firstError)) {
            errorMessage = firstError[0];
          } else if (typeof firstError === "string") {
            errorMessage = firstError;
          }
        } else if (typeof errorData === "string") {
          errorMessage = errorData;
        }
      }

      // Mostrar popup de error
      setNotification({ open: true, message: errorMessage, isError: true });

      // Re-lanzar el error para que ClientModal también lo maneje si es necesario
      throw error;
    }
  };

  return (
    <>
      {(clientsListError || clientDetailError) && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error">
            Error al cargar los clientes. Inténtalo de nuevo.
          </Alert>
        </Box>
      )}

      {!clientsListError && !clientDetailError && (
        <>
          {isDetailView && viewedClient ? (
            /* Vista detallada: ficha del cliente */
            <ClientReadOnlyScope readOnly={Boolean(clientDetailData?.is_read_only)}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  alignItems: "stretch",
                }}
              >
                {clientDetailData?.is_read_only && (
                  <AlertNote icon={LockKeyhole}>
                    Este cliente está en modo solo lectura. Puedes consultar sus datos, proyectos y contenidos, pero no realizar cambios.
                  </AlertNote>
                )}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    alignItems: "stretch",
                    "@media (min-width:1600px)": {
                      flexDirection: "row",
                    },
                  }}
                >
                  {/* Sección de onboarding: lado izquierdo (50%) */}
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      minHeight: 0,
                      flex: 1,
                      minWidth: 0,
                    }}
                  >
                    <OnboardingFormSection
                      ref={onboardingFormRef}
                      clientId={viewClientId}
                      refreshKey={clientDetailRefreshKey}
                      clientDetailData={clientDetailData}
                      clientDetailLoading={
                        clientDetailIsLoading || clientDetailIsInitialLoading
                      }
                      clientDetailError={clientDetailError}
                      onSuccess={(message) =>
                        setNotification({ open: true, message, isError: false })
                      }
                      onError={(message) =>
                        setNotification({ open: true, message, isError: true })
                      }
                    />
                  </Box>

                  {/* Sección de invitación: lado derecho (50%) */}
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      minHeight: 0,
                      flex: 1,
                      minWidth: 0,
                    }}
                  >
                    <InvitationLinkSection
                      clientId={viewClientId}
                      refreshKey={clientDetailRefreshKey}
                      clientDetailData={clientDetailData}
                      clientDetailLoading={
                        clientDetailIsLoading || clientDetailIsInitialLoading
                      }
                      clientDetailError={clientDetailError}
                      onSuccess={(message) =>
                        setNotification({ open: true, message, isError: false })
                      }
                      onError={(message) =>
                        setNotification({ open: true, message, isError: true })
                      }
                    />
                  </Box>
                </Box>

                <ClientUsersSection
                  clientId={viewClientId}
                  refreshKey={clientDetailRefreshKey}
                  clientDetailData={clientDetailData}
                  clientDetailLoading={
                    clientDetailIsLoading || clientDetailIsInitialLoading
                  }
                  clientDetailError={clientDetailError}
                  onSuccess={(message) =>
                    setNotification({ open: true, message, isError: false })
                  }
                  onError={(message) =>
                    setNotification({ open: true, message, isError: true })
                  }
                />
              </Box>
            </ClientReadOnlyScope>
          ) : (
            /* Vista de lista: tabla (por defecto) o tarjetas */
            <Stack spacing={2}>
              {listViewMode === "table" ? (
                <DataTable
                  {...clientsToolbarProps}
                  columns={clientTableColumns}
                  rows={filteredClients}
                  loading={showClientsSkeleton || clientsListIsFetching}
                  skeletonRows={8}
                  emptyIcon={Users}
                  emptyMessage={emptyStateMessage}
                  tableMinWidth={1120}
                  stickyFirstColumn
                  renderRow={(client, _index, visibleColumns) => (
                    <CompactClientCard
                      client={client}
                      layout="row"
                      columns={visibleColumns}
                      {...clientCardProps}
                    />
                  )}
                  page={Math.max(clientsPage - 1, 0)}
                  rowsPerPage={tableRowsPerPage}
                  totalCount={typeof totalClients === "number" ? totalClients : filteredClients.length}
                  onPageChange={handleTablePageChange}
                  onRowsPerPageChange={handleTableRowsPerPageChange}
                />
              ) : (
                <>
                  <DataTableToolbar variant="standalone" {...clientsToolbarProps} />
                  <Box
                    sx={{
                      display: "grid",
                      gap: 2,
                      // Una columna hasta md; a partir de ahí tantas como quepan,
                      // sin que el mínimo de 525px desborde un contenedor estrecho.
                      gridTemplateColumns: {
                        xs: "minmax(0, 1fr)",
                        md: "repeat(auto-fill, minmax(min(100%, 525px), 1fr))",
                      },
                    }}
                  >
                    {showClientsSkeleton ? (
                      Array.from({ length: 6 }).map((_, index) => (
                        <CompactClientCardSkeleton key={`client-skeleton-${index}`} />
                      ))
                    ) : filteredClients.length === 0 && !clientsListIsFetching ? (
                      <Box key="empty-state" sx={{ gridColumn: "1 / -1" }}>
                        <EmptyStateMessage
                          icon={Users}
                          message={emptyStateMessage}
                          sx={{ py: 6 }}
                        />
                      </Box>
                    ) : (
                      filteredClients.map((client) => (
                        <Box key={client.id} sx={{ width: "100%" }}>
                          <CompactClientCard client={client} {...clientCardProps} />
                        </Box>
                      ))
                    )}
                    {!showClientsSkeleton &&
                      isLoadingMoreClients &&
                      hasMoreClients &&
                      Array.from({ length: 3 }).map((_, index) => (
                        <CompactClientCardSkeleton key={`client-skeleton-more-${index}`} />
                      ))}
                  </Box>
                </>
              )}
              {!isDetailView && !isTableView && hasMoreClients && (
                <Box ref={loadMoreRef} sx={{ height: 1 }} />
              )}
            </Stack>
          )}
        </>
      )}

      <IncidentFiltersModal
        open={isFilterModalOpen}
        onClose={handleCloseFilterModal}
        title="Filtros avanzados"
        filterConfigs={filterConfigs}
        draftFilters={draftAdvancedFilters}
        onChange={handleDraftFilterChange}
        onApply={handleApplyAdvancedFilters}
        onClearAll={handleClearDraftFilters}
      />

      {/* Modales */}

      <PlanLimitDialog
        open={Boolean(planLimitError)}
        onClose={() => setPlanLimitError(null)}
        kind={planLimitError?.kind || "clients"}
        planId={planLimitError?.plan}
        planName={planLimitError?.planName}
        limit={planLimitError?.limit}
      />

      {showNewClientModal && (
        <ClientModal
          isOpen={true}
          onClose={() => setShowNewClientModal(false)}
          onSave={handleNewClientSave}
          agenciesRefresh={agenciesRefresh}
          onError={(message) =>
            setNotification({ open: true, message, isError: true })
          }
          onSuccess={(message) =>
            setNotification({ open: true, message, isError: false })
          }
        />
      )}

      {createdClient && (
        <ClientCreatedModal
          open
          client={createdClient}
          onSkip={() => setCreatedClient(null)}
          onStart={() => {
            setSeoSetupClient(createdClient);
            setCreatedClient(null);
          }}
        />
      )}

      {seoSetupClient && (
        <SeoConnectModal
          open
          onOpenChange={(open) => !open && setSeoSetupClient(null)}
          clientId={seoSetupClient.id}
          client={seoSetupClient.name ? seoSetupClient : undefined}
          currentLink={null}
          onConnected={() => loadClients()}
        />
      )}

      <SuccessPopup
        message={notification.message}
        isOpen={notification.open}
        onClose={() =>
          setNotification({ open: false, message: "", isError: false })
        }
        error={notification.isError}
        autoHideDuration={notification.isError ? 4000 : undefined}
      />
    </>
  );
}
